import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { Sheet as SheetRaw, SheetContent as SheetContentRaw, SheetTitle as SheetTitleRaw } from '@/components/ui/sheet';

// ui/sheet.tsx is untyped (forwardRef without props): loose types, as in ConfirmDialog.
type Loose = ComponentType<Record<string, unknown> & { children?: ReactNode }>;
const Sheet = SheetRaw as unknown as Loose;
const SheetContent = SheetContentRaw as unknown as Loose;
const SheetTitle = SheetTitleRaw as unknown as Loose;

/**
 * The rail on a narrow screen (W1, DES-10): a menu button opens the same rail
 * in a drawer. `breakpoint` is the width from which the fixed rail shows, so
 * the button hides there.
 */
export function RailDrawerButton({ onOpen, breakpoint }: { onOpen: () => void; breakpoint: 'md' | 'lg' }) {
  const { t } = useTranslation(['dialer']);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('dialer:nav.openMenu')}
      title={t('dialer:nav.openMenu')}
      className={`${breakpoint === 'md' ? 'md:hidden' : 'lg:hidden'} flex h-11 w-11 shrink-0 items-center justify-center rounded-md`}
      style={{ border: '1px solid var(--vf-border)', background: 'var(--vf-surface-2)', color: 'var(--vf-text-1)' }}
    >
      <Menu size={18} aria-hidden />
    </button>
  );
}

export function RailDrawer({ open, onOpenChange, title, children }: { open: boolean; onOpenChange: (open: boolean) => void; title: string; children: ReactNode }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* QA-W5-8: a navigation drawer has a title and no description; say so to Radix (no console warning). */}
      <SheetContent side="left" className="w-[224px] p-0" style={{ background: 'var(--vf-surface-1)' }} aria-describedby={undefined}>
        <SheetTitle className="sr-only">{title}</SheetTitle>
        {children}
      </SheetContent>
    </Sheet>
  );
}

/** Folded state of a space's rail, remembered per browser (convenience only). */
export function readRailCollapsed(space: string): boolean {
  try { return localStorage.getItem(`xdial_rail_collapsed_${space}`) === '1'; } catch { return false; }
}

export function writeRailCollapsed(space: string, collapsed: boolean): void {
  try { localStorage.setItem(`xdial_rail_collapsed_${space}`, collapsed ? '1' : '0'); } catch { /* storage unavailable */ }
}
